import React, { useCallback, useState, useEffect, useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Typography,
  Radio,
  Backdrop,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/CloseRounded';
import { AddBox } from '@mui/icons-material';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import Country from '../../../features/CompanycodeTranslation/components/country';
import State from '../../../features/CompanycodeTranslation/components/state';
import District from '../../../features/CompanycodeTranslation/components/district';
import City from '../../../features/CompanycodeTranslation/components/city';
import Pincode from '../../../features/CompanycodeTranslation/components/pincode';
import { useLazyGetDistrictOptionsQuery } from '../../../features/CompanycodeTranslation/Api/countryApi';
import { useLazyFetchPincodeLocationQuery } from '../../../features/CompanycodeTranslation/Api/pincodeLocation.api';
import { showWarningSnackbar } from '../../../components/snackBar/snakbar';
import { FetchGstInDetails } from '../../../library/GstinFetch';
import AddressDetailsTextField from '../../../components/formElements/AddressDetailsTextField';

const MAX_ROWS = 10;
const GSTIN_FORMAT_REGEX = /\d{2}[A-Z]{5}\d{4}[A-Z\d]{2}[A-Z]{1}[A-Z\d]{1}$/;

/* ------------------------- MEMOIZED ROW COMPONENT ------------------------- */

const AddressRow = React.memo(
  ({
    index,
    control,
    errors,
    remove,
    clearErrors,
    setError,
    setValue,
    getValues,
    trigger,
    total,
    stateLists,
    setStateLists,
    districtsLists,
    setDistrictsLists,
    getDistrictOptions,
    cityOptions,
    setCityOptions,
    previousStates,
    setPreviousStates,
    pincodeData,
    getDistrictOptionsForState,
    cityRef,
    syncToFormik,
    viewPage = false,
    sameAsAbove = false,
    addressTitleRefs,
    allAddresses,
    onSameAsAboveChange,
    replace,
    setBackdropOpen,
    addressMaxLength,
  }) => {
    console.log(sameAsAbove, 'sameAsAbove');

    // Watch only this row — isolates rerenders
    const row = useWatch({
      control,
      name: `addresses.${index}`,
    });
    console.log(row, 'row');

    // Disable all fields except nickname for first active row when sameAsAbove is true
    // Find the first active (non-expired) row index
    const firstActiveRowIndex = allAddresses?.findIndex((addr) => (addr?.expiredtimestamp ?? 0) === 0) ?? 0;
    const isFirstRowDisabled = index === firstActiveRowIndex && sameAsAbove === true;

    // Fetch GST details for this row's consignee GSTIN and fill row fields (same as main GSTIN in AddCustomer)
    const handleConsigneeGstinFetch = useCallback(
      async (gstinTrimmed) => {
        if (!gstinTrimmed || gstinTrimmed.length !== 15) return;
        if (setBackdropOpen) setBackdropOpen(true);
        try {
          const fetchData = await FetchGstInDetails(gstinTrimmed);
          if (fetchData?.data) {
            clearErrors(`addresses.${index}.consigneeGstin`);
            const d = fetchData.data;
            setValue(`addresses.${index}.consigneeName`, d.LegalName ?? d.TradeName ?? '', { shouldValidate: true });
            setValue(`addresses.${index}.address`, d.AddressTest ?? '', { shouldValidate: true });
            const pin = d.Address?.PinCode ?? '';
            setValue(`addresses.${index}.pincode`, pin, { shouldValidate: true });
            setValue(`addresses.${index}.country`, 'IndiaState', { shouldValidate: true });
            if (pin && String(pin).replace(/\D/g, '').length === 6) {
              try {
                const { data } = await pincodeData({ pincode: String(pin).replace(/\D/g, '') }, true);
                if (data) {
                  const newState = data.State + 'District';
                  setValue(`addresses.${index}.state`, newState, { shouldValidate: true });
                  setValue(`addresses.${index}.district`, data.District + 'District', { shouldValidate: true });
                  setCityOptions((prev) => ({ ...prev, [index]: data?.City?.[0] ? data.City : [] }));
                  if (previousStates[index] !== newState) {
                    const districtResponse = await getDistrictOptionsForState({ codetype: newState }, true);
                    if (districtResponse?.data) {
                      setDistrictsLists((prev) => ({ ...prev, [index]: districtResponse.data }));
                    }
                    setPreviousStates((prev) => ({ ...prev, [index]: newState }));
                  }
                }
              } catch (err) {
                // Pincode API may fail; address/name/pincode already set
              }
            }
            syncToFormik();
          }
        } catch (error) {
          if (error?.code === 'F08000' || error?.code === 'SWEB_9035') {
            setError(`addresses.${index}.consigneeGstin`, { type: 'manual', message: 'Invalid GSTIN' });
          } else {
            clearErrors(`addresses.${index}.consigneeGstin`);
          }
        } finally {
          if (setBackdropOpen) setBackdropOpen(false);
        }
      },
      [
        index,
        setValue,
        pincodeData,
        getDistrictOptionsForState,
        setCityOptions,
        setDistrictsLists,
        setPreviousStates,
        previousStates,
        syncToFormik,
      ]
    );

    // Set default row - use actual array indices of all active rows
    const handleSetDefault = () => {
      // Get all active (non-expired) row indices
      const activeIndices = [];
      if (allAddresses && Array.isArray(allAddresses)) {
        allAddresses.forEach((addr, idx) => {
          const expiredtimestamp = addr?.expiredtimestamp ?? 0;
          if (expiredtimestamp === 0) {
            activeIndices.push(idx);
          }
        });
      }

      // Set isDefault for all active rows using their actual array indices
      activeIndices.forEach((activeIndex) => {
        setValue(`addresses.${activeIndex}.isDefault`, activeIndex === index ? 1 : 0);
      });

      setTimeout(() => {
        syncToFormik();
      }, 100);
    };

    // Handle Country change
    const handleCountryChange = useCallback(
      (e, option, name) => {
        const fieldName = `addresses.${index}.country`;
        if (option) {
          setValue(fieldName, option.value, { shouldValidate: true });
          // Always reset state and district when country changes
          setValue(`addresses.${index}.state`, '', { shouldValidate: true });
          setValue(`addresses.${index}.district`, '', { shouldValidate: true });
          setStateLists((prev) => ({ ...prev, [index]: [] }));
          setDistrictsLists((prev) => ({ ...prev, [index]: [] }));
          setPreviousStates((prev) => ({ ...prev, [index]: undefined }));
          setCityOptions((prev) => ({ ...prev, [index]: [] }));
          setTimeout(() => trigger(`addresses.${index}.pincode`), 100);
        } else {
          setValue(fieldName, '', { shouldValidate: true });
          setValue(`addresses.${index}.state`, '', { shouldValidate: true });
          setValue(`addresses.${index}.district`, '', { shouldValidate: true });
          setStateLists((prev) => ({ ...prev, [index]: [] }));
          setDistrictsLists((prev) => ({ ...prev, [index]: [] }));
          setPreviousStates((prev) => ({ ...prev, [index]: undefined }));
          setCityOptions((prev) => ({ ...prev, [index]: [] }));
          setTimeout(() => trigger(`addresses.${index}.pincode`), 100);
        }
        setTimeout(() => syncToFormik(), 100);
      },
      [index, setValue, trigger, setStateLists, setDistrictsLists, setPreviousStates, setCityOptions]
    );

    // Handle State change
    const handleStateChange = useCallback(
      async (e, option, name, statesList) => {
        const fieldName = `addresses.${index}.state`;
        if (option) {
          setValue(fieldName, option.value);
          // Update state list for this row
          if (statesList) {
            setStateLists((prev) => ({
              ...prev,
              [index]: statesList,
            }));
          }
          // Fetch districts when state changes
          if (option.value && row?.country === 'IndiaState') {
            try {
              const districtResponse = await getDistrictOptions({ codetype: option.value }, true);
              if (districtResponse.data) {
                setDistrictsLists((prev) => ({
                  ...prev,
                  [index]: districtResponse.data,
                }));
              }
            } catch (error) {
              console.error('Error fetching districts:', error);
            }
          }
          // Reset dependent fields when state changes
          if (option.value !== row?.state) {
            setValue(`addresses.${index}.district`, '');
            setValue(`addresses.${index}.city`, '');
          }
        } else {
          setValue(fieldName, '');
          setValue(`addresses.${index}.district`, '');
          setValue(`addresses.${index}.city`, '');
        }
        // Sync to Formik after state change
        setTimeout(() => {
          syncToFormik();
        }, 100);
      },
      [row?.state]
    );

    // Handle District change
    const handleDistrictChange = useCallback(
      (e, option, name) => {
        const fieldName = `addresses.${index}.district`;
        if (option) {
          setValue(fieldName, option.value);
          // Reset dependent fields when district changes
          if (option.value !== row?.district) {
            setValue(`addresses.${index}.city`, '');
          }
        } else {
          setValue(fieldName, '');
          setValue(`addresses.${index}.city`, '');
        }
        // Sync to Formik after district change
        setTimeout(() => {
          syncToFormik();
        }, 100);
      },
      [row?.district]
    );

    return (
      <TableRow>
        {/* Default Radio */}
        <TableCell align="center">
          <Radio checked={row?.isDefault === 1 || row?.isDefault === true} onChange={handleSetDefault} disabled={viewPage} />
        </TableCell>

        {/* Nickname (Required + Unique) */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.addressTitle`}
            rules={{
              required: 'Branch/ Godown Name is required',
              validate: (value) => {
                const current = value?.trim()?.toLowerCase();
                if (!current) return true;

                // Get all addresses from form using getValues
                const allAddresses = getValues('addresses') || [];
                // Check for duplicates (case-insensitive, excluding expired rows and current row)
                let duplicateFound = false;
                for (let i = 0; i < allAddresses.length; i++) {
                  if (i === index) continue; // Skip current row

                  const otherRow = allAddresses[i];
                  const expiredtimestamp = otherRow?.expiredtimestamp ?? 0;

                  // Skip expired rows
                  if (expiredtimestamp !== 0) continue;

                  const otherTitle = (otherRow?.addressTitle ?? '').trim().toLowerCase();
                  if (otherTitle === current) {
                    duplicateFound = true;
                    break;
                  }
                }

                return duplicateFound ? 'Branch/ Godown Name must be unique' : true;
              },
            }}
            render={({ field, fieldState }) => {
              // Create or get ref for this field
              if (!addressTitleRefs.current[index]) {
                addressTitleRefs.current[index] = React.createRef();
              }

              return (
                <TextField
                  {...field}
                  inputRef={addressTitleRefs.current[index]}
                  size="small"
                  fullWidth
                  placeholder={`Godown ${index + 1}`}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={viewPage}
                  sx={{
                    '& .MuiInputBase-input::placeholder': {
                      color: 'grey',
                      opacity: 1,
                    },
                  }}
                  onChange={(e) => {
                    const input = e.target;
                    const cursor = input.selectionStart;

                    const raw = input.value.slice(0, 50);

                    const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());

                    field.onChange(formatted);
                    setTimeout(() => {
                      trigger(`addresses.${index}.addressTitle`);
                      syncToFormik();
                    }, 500);
                    requestAnimationFrame(() => {
                      input.setSelectionRange(cursor, cursor);
                    });
                  }}
                  onBlur={(e) => {
                    field.onBlur(e);
                  }}
                />
              );
            }}
          />
        </TableCell>

        {/* Consignee GSTIN */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.consigneeGstin`}
            rules={{
              validate: (value) => {
                const trimmed = (value || '').trim();
                if (!trimmed) return true;
                if (!GSTIN_FORMAT_REGEX.test(trimmed)) return 'Invalid GSTIN Format';
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                fullWidth
                disabled={viewPage || isFirstRowDisabled}
                inputProps={{ maxLength: 15 }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                onChange={async (e) => {
                  const raw = e.target.value;
                  const capitalized = raw.toUpperCase();
                  field.onChange(capitalized);
                  const gstinTrimmed = (raw || '').trim();
                  if (gstinTrimmed.length === 15) {
                    trigger(`addresses.${index}.consigneeGstin`);
                  }
                  if (gstinTrimmed.length === 15) {
                    await handleConsigneeGstinFetch(gstinTrimmed);
                  }
                }}
                onBlur={(e) => {
                  field.onBlur(e);
                  const hasApiInvalidError = errors?.[index]?.consigneeGstin?.message === 'Invalid GSTIN';
                  if (!hasApiInvalidError) {
                    trigger(`addresses.${index}.consigneeGstin`);
                  }
                  syncToFormik();
                }}
              />
            )}
          />
        </TableCell>
        {/* Consignee Name */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.consigneeName`}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                fullWidth
                disabled={viewPage || isFirstRowDisabled}
                onChange={(e) => {
                  const input = e.target;
                  const cursor = input.selectionStart;

                  const raw = input.value.slice(0, 50);

                  const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
                  field.onChange(formatted);
                  requestAnimationFrame(() => {
                    input.setSelectionRange(cursor, cursor);
                  });
                }}
                onBlur={(e) => {
                  field.onBlur(e);
                  syncToFormik();
                }}
              />
            )}
          />
        </TableCell>
        {/* Country */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.country`}
            render={({ field }) => (
              <Country
                name={`addresses.${index}.country`}
                size="small"
                // label="Country"
                value={field.value || ''}
                disabled={viewPage || isFirstRowDisabled}
                onChange={handleCountryChange}
              />
            )}
          />
        </TableCell>

        {/* Pincode */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.pincode`}
            rules={{
              validate: (value) => {
                // Get country value for this row using getValues to ensure we have the latest value
                const country = getValues(`addresses.${index}.country`) || row?.country || '';
                const isIndiaState = country === 'IndiaState';

                // If no value, no validation error
                if (!value || value.trim() === '') {
                  return true;
                }

                // For IndiaState: must be exactly 6 digits
                if (isIndiaState) {
                  if (value.length !== 6) {
                    return 'Pincode must be 6 digits';
                  }
                  // Check if all digits are numeric
                  if (!/^\d{6}$/.test(value)) {
                    return 'Pincode must contain only numbers';
                  }
                } else {
                  // For other countries: can be up to 10 digits
                  if (value.length > 10) {
                    return 'Pincode can be up to 10 digits';
                  }
                  // Check if all digits are numeric
                  if (!/^\d+$/.test(value)) {
                    return 'Pincode must contain only numbers';
                  }
                }

                return true;
              },
            }}
            render={({ field, fieldState }) => {
              // Get country value for this row
              const country = row?.country || '';
              const isIndiaState = country === 'IndiaState';
              const maxLength = isIndiaState ? 6 : 10;

              // Handle pincode keydown to restrict non-numeric input

              return (
                <Pincode
                  name={`addresses.${index}.pincode`}
                  size="small"
                  // placeholder="Pincode"
                  value={field.value || ''}
                  onChange={async (e) => {
                    const value = e.target.value;

                    // Filter out non-numeric characters
                    const numericPin = value.replace(/\D/g, '');

                    // Set max length based on country
                    const trimmedPin = numericPin.slice(0, maxLength);

                    // Update field value immediately - this is synchronous and won't cause focus loss
                    field.onChange(trimmedPin);

                    // Trigger validation after value change
                    trigger(`addresses.${index}.pincode`);
                    if (!isIndiaState) {
                      setValue(`addresses.${index}.pincode`, trimmedPin);
                      syncToFormik();
                    }
                    // Reset dependent fields when pincode is empty or incomplete
                    if (!trimmedPin || trimmedPin.length < (isIndiaState ? 6 : 1)) {
                      setValue(`addresses.${index}.state`, '');
                      setValue(`addresses.${index}.district`, '');
                      setValue(`addresses.${index}.city`, '');
                      setValue(`addresses.${index}.address`, '');
                      setCityOptions((prev) => ({
                        ...prev,
                        [index]: [],
                      }));
                      syncToFormik();
                      // Don't sync to Formik during typing - only on blur to prevent focus loss
                      return;
                    }

                    // Only call API for IndiaState when pincode is exactly 6 digits
                    if (isIndiaState && trimmedPin.length === 6) {
                      try {
                        const { data } = await pincodeData({ pincode: trimmedPin }, true);
                        const newState = data.State + 'District';

                        // Set state, district, country
                        setValue(`addresses.${index}.state`, newState);
                        setValue(`addresses.${index}.district`, data.District + 'District');
                        setValue(`addresses.${index}.country`, 'IndiaState');

                        // Set city options
                        setCityOptions((prev) => ({
                          ...prev,
                          [index]: data?.City?.[0] ? data.City : [],
                        }));
                        syncToFormik();
                        // Fetch districts if state changed
                        if (previousStates[index] !== newState) {
                          try {
                            const districtResponse = await getDistrictOptionsForState({ codetype: newState }, true);
                            if (districtResponse.data) {
                              setDistrictsLists((prev) => ({
                                ...prev,
                                [index]: districtResponse.data,
                              }));
                            }
                            setPreviousStates((prev) => ({
                              ...prev,
                              [index]: newState,
                            }));
                          } catch (error) {
                            console.error('Error fetching districts:', error);
                          }
                        }
                      } catch (error) {
                        console.error('Error fetching pincode data:', error);
                        showWarningSnackbar(
                          '"We couldn’t find details for this PIN code. Please verify the PIN code or enter the details manually."'
                        );
                      } finally {
                        if (cityRef.current) {
                          cityRef.current.focus();
                        }
                      }
                    }
                    // Don't sync to Formik during regular typing - only on blur or after API calls
                    // This prevents focus loss while typing
                  }}
                  onBlur={async (e) => {
                    field.onBlur(e);
                    // Trigger validation on blur
                    trigger(`addresses.${index}.pincode`);
                    // Call pincode API on blur when pincode has 6 digits (for every edit)
                    const trimmedPin = (field.value || '').replace(/\D/g, '');
                    if (isIndiaState && trimmedPin.length === 6) {
                      try {
                        const { data } = await pincodeData({ pincode: trimmedPin }, true);
                        if (data) {
                          const newState = data.State + 'District';
                          setValue(`addresses.${index}.state`, newState);
                          setValue(`addresses.${index}.district`, data.District + 'District');
                          setValue(`addresses.${index}.country`, 'IndiaState');
                          setCityOptions((prev) => ({ ...prev, [index]: data?.City?.[0] ? data.City : [] }));
                          if (previousStates[index] !== newState) {
                            const districtResponse = await getDistrictOptionsForState({ codetype: newState }, true);
                            if (districtResponse?.data) {
                              setDistrictsLists((prev) => ({ ...prev, [index]: districtResponse.data }));
                            }
                            setPreviousStates((prev) => ({ ...prev, [index]: newState }));
                          }
                        }
                      } catch (err) {
                        // Silently ignore - user may have invalid pincode
                      }
                    }
                    syncToFormik();
                  }}
                  error={!!fieldState.error}
                  errormsg={fieldState.error?.message}
                  disable={viewPage || isFirstRowDisabled}
                  required={false}
                  maxLength={maxLength}
                />
              );
            }}
          />
        </TableCell>

        {/* State */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.state`}
            render={({ field }) =>
              row?.country === 'IndiaState' ? (
                <State
                  name={`addresses.${index}.state`}
                  size="small"
                  // label="State"
                  value={field.value || ''}
                  disabled={viewPage || isFirstRowDisabled}
                  onStateChange={handleStateChange}
                  setStateList={(list) => {
                    setStateLists((prev) => ({
                      ...prev,
                      [index]: list,
                    }));
                  }}
                  stateList={stateLists[index] || []}
                  error={!!errors?.[index]?.state}
                  errormessage={errors?.[index]?.state?.message}
                />
              ) : (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  // placeholder="State"
                  disabled={viewPage || isFirstRowDisabled}
                  error={!!errors?.[index]?.state}
                  helperText={errors?.[index]?.state?.message}
                  onChange={(e) => {
                    const input = e.target;
                    const cursor = input.selectionStart;

                    const raw = input.value.slice(0, 50);

                    const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
                    field.onChange(formatted);
                    requestAnimationFrame(() => {
                      input.setSelectionRange(cursor, cursor);
                    });
                  }}
                  onBlur={(e) => {
                    field.onBlur(e);
                    syncToFormik();
                  }}
                />
              )
            }
          />
        </TableCell>

        {/* District */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.district`}
            render={({ field }) =>
              row?.country === 'IndiaState' ? (
                <District
                  name={`addresses.${index}.district`}
                  size="small"
                  // label="District"
                  value={field.value || ''}
                  disabled={viewPage || isFirstRowDisabled}
                  required={false}
                  stateVal={row?.state || ''}
                  list={districtsLists[index] || []}
                  onDistrictChange={handleDistrictChange}
                  error={!!errors?.[index]?.district}
                  errormessage={errors?.[index]?.district?.message}
                />
              ) : (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  // placeholder="District"
                  disabled={viewPage || isFirstRowDisabled}
                  error={!!errors?.[index]?.district}
                  helperText={errors?.[index]?.district?.message}
                  onChange={(e) => {
                    const input = e.target;
                    const cursor = input.selectionStart;

                    const raw = input.value.slice(0, 50);

                    const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
                    field.onChange(formatted);
                    requestAnimationFrame(() => {
                      input.setSelectionRange(cursor, cursor);
                    });
                  }}
                  onBlur={(e) => {
                    field.onBlur(e);
                    syncToFormik();
                  }}
                />
              )
            }
          />
        </TableCell>

        {/* Address */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.address`}
            render={({ field }) => (
              <AddressDetailsTextField
                name={`addresses.${index}.address`}
                label="Address Details"
                value={field.value}
                disabled={viewPage || isFirstRowDisabled}
                maxLength={addressMaxLength}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={(e) => {
                  field.onBlur(e);
                  syncToFormik();
                }}
              />
            )}
          />
        </TableCell>

        {/* City */}
        <TableCell>
          <Controller
            control={control}
            name={`addresses.${index}.city`}
            render={({ field }) => (
              <City
                name={`addresses.${index}.city`}
                size="small"
                disabled={viewPage || isFirstRowDisabled}
                options={cityOptions[index] || []}
                required={false}
                value={field.value || ''}
                onChange={(e, newValue) => {
                  // Handle dropdown selection from Autocomplete options.
                  if (typeof newValue === 'string') {
                    const selectedValue = newValue.slice(0, 50).replace(/\b\w/g, (l) => l.toUpperCase());
                    field.onChange(selectedValue);
                    setTimeout(() => {
                      syncToFormik();
                    }, 100);
                    return;
                  }

                  // Handle manual typing in the input.
                  const input = e?.target;
                  const cursor = input?.selectionStart ?? 0;
                  const raw = (input?.value || '').slice(0, 50);
                  const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
                  field.onChange(formatted);

                  // Sync to Formik after city change
                  setTimeout(() => {
                    syncToFormik();
                  }, 100);

                  requestAnimationFrame(() => {
                    if (input && typeof input.setSelectionRange === 'function') {
                      input.setSelectionRange(cursor, cursor);
                    }
                  });
                }}
                inputRef={cityRef}
                error={false}
                errormsg={''}
              />
            )}
          />
        </TableCell>

        {/* DELETE ROW */}
        <TableCell align="left">
          {/* {row?.isDefault !== 1 && ( */}
          <IconButton
            onClick={() => {
              // If deleting the first active row and "same as above" is checked, uncheck it
              if (index === 0 && sameAsAbove) {
                onSameAsAboveChange(false);
                remove(index);
                syncToFormik();
              }

              // Remove the row from RHF array
              remove(index);
              // Sync updated array to Formik
              setTimeout(() => {
                syncToFormik();
              }, 0);
            }}
            disabled={viewPage || row?.isDefault === 1}
            sx={{
              margin: '0px 2px',
              padding: '5px',
              backgroundColor: '#fbc7c78c',
            }}
          >
            {row?.isDefault === 1 ? (
              <Tooltip title="Unable to delete default address" placement="bottom">
                <DeleteIcon
                  sx={{
                    fontSize: '18px',
                    color: '#eb6a6a',
                  }}
                />
              </Tooltip>
            ) : (
              <DeleteIcon
                sx={{
                  fontSize: '18px',
                  color: '#eb6a6a',
                }}
              />
            )}
          </IconButton>
          {/* )} */}
        </TableCell>
      </TableRow>
    );
  },
  (prev, next) => {
    // Prevent re-render if the row didn't change AND districtsLists for this index didn't change
    const rowChanged = JSON.stringify(prev.row) !== JSON.stringify(next.row);
    const districtsChanged = JSON.stringify(prev.districtsLists?.[prev.index]) !== JSON.stringify(next.districtsLists?.[next.index]);
    const stateListsChanged = JSON.stringify(prev.stateLists?.[prev.index]) !== JSON.stringify(next.stateLists?.[next.index]);
    const cityOptionsChanged = JSON.stringify(prev.cityOptions?.[prev.index]) !== JSON.stringify(next.cityOptions?.[next.index]);

    // Re-render if row, districts, stateLists, or cityOptions changed
    if (rowChanged || districtsChanged || stateListsChanged || cityOptionsChanged) {
      return false; // Allow re-render
    }

    // Also check if other props changed
    return prev.index === next.index && prev.total === next.total && JSON.stringify(prev.errors) === JSON.stringify(next.errors);
  }
);

AddressRow.displayName = 'AddressRow';

/* --------------------------- MAIN COMPONENT --------------------------- */

export default function RHFDynamicAddressRows({
  formik,
  fieldName = 'deliveryAddresses',
  viewPage = false,
  loc,
  id,
  resetbool,
  sameAsAbove = false,
  focusRowIndex = null,
  onSameAsAboveChange,
  addressMaxLength = 500,
}) {
  console.log(sameAsAbove, 'sameAsAbove');

  // Ref to prevent infinite loops when syncing with Formik
  const isSyncingRef = useRef(false);
  const isInitializedRef = useRef(false);
  // Refs for nickname fields to focus on errors
  const addressTitleRefs = useRef({});
  // Default initial values
  const defaultInitialValues = [
    {
      addressTitle: '',
      consigneeName: '',
      consigneeGstin: '',
      country: 'IndiaState',
      state: '',
      district: '',
      address: '',
      city: '',
      pincode: '',
      isDefault: 1,
      expiredtimestamp: 0,
    },
  ];

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setValue,
    setFocus,
    setError,
    clearErrors,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      addresses: defaultInitialValues,
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'addresses',
  });
  console.log(fields, 'fields');

  // State lists for each row (for State component)
  const [stateLists, setStateLists] = useState({});
  // District lists for each row (for District component)
  const [districtsLists, setDistrictsLists] = useState({});
  // City options for each row (for City component)
  const [cityOptions, setCityOptions] = useState({});
  // Previous states for each row (to avoid refetching districts)
  const [previousStates, setPreviousStates] = useState({});
  // Backdrop state for loading
  const [backdropOpen, setBackdropOpen] = useState(false);
  // Refs for city fields (one per row)
  const cityRefs = useRef({});

  // Hook to fetch district options
  const [getDistrictOptions] = useLazyGetDistrictOptionsQuery();
  // Hook to fetch pincode location data
  const [pincodeData] = useLazyFetchPincodeLocationQuery();

  // Wrapper function for getDistrictOptions to use in AddressRow
  const getDistrictOptionsForState = useCallback(async (params, shouldFetch) => {
    setBackdropOpen(true);
    try {
      const response = await getDistrictOptions(params, shouldFetch);
      return response;
    } finally {
      setBackdropOpen(false);
    }
  }, []);

  const values = watch('addresses');

  // Count only active (non-expired) rows
  const activeRowCount = useMemo(() => {
    if (!values || values.length === 0) return 0;
    return values.filter((row) => (row?.expiredtimestamp ?? 0) === 0).length;
  }, [values]);

  // Normalize isDefault values from boolean to numeric (1/0) and ensure expiredtimestamp exists
  const normalizeAddresses = useCallback((addresses) => {
    if (!Array.isArray(addresses)) return [];
    return addresses.map((addr) => ({
      ...addr,
      isDefault: addr.isDefault === true || addr.isDefault === 1 ? 1 : 0,
      expiredtimestamp: addr.expiredtimestamp ?? 0,
    }));
  }, []);

  // Track if we've loaded data from Formik (using stringified value to detect changes)
  const loadedFormikDataRef = useRef(null);

  // In view mode, depend on Formik content so we re-run when getPartyMasterData sets deliveryAddresses
  const formikDeliveryKey = viewPage && Array.isArray(formik?.values?.[fieldName]) ? JSON.stringify(formik.values[fieldName]) : '';

  // Initialize from Formik values once when data is available (or reset when Formik is cleared)
  useEffect(() => {
    const formikAddresses = formik?.values?.[fieldName];

    if (!Array.isArray(formikAddresses)) {
      return;
    }

    // When Formik has empty array (e.g. after reset), sync RHF to empty/default state
    if (formikAddresses.length === 0) {
      isSyncingRef.current = true;
      replace(defaultInitialValues);
      reset({ addresses: defaultInitialValues }, { keepDefaultValues: false });
      isInitializedRef.current = false;
      loadedFormikDataRef.current = null;
      setTimeout(() => {
        isSyncingRef.current = false;
      }, 50);
      return;
    }

    // Create a string representation of the data to check if we've already loaded it
    const formikDataStr = JSON.stringify(formikAddresses);

    // Check if current form has meaningful data that matches Formik data
    const currentValues = watch('addresses');
    // Initialize if Formik has values and we haven't initialized yet
    if (formikAddresses.length > 0 && !isInitializedRef.current) {
      console.log('Initializing from Formik values:', formikAddresses);
      isSyncingRef.current = true;
      const normalizedAddresses = normalizeAddresses(formikAddresses);

      // First replace to update fields array
      replace(normalizedAddresses);

      // Then reset form values
      reset({ addresses: normalizedAddresses }, { keepDefaultValues: false });

      // Manually set each field to ensure values are set
      normalizedAddresses.forEach((addr, idx) => {
        setValue(`addresses.${idx}.addressTitle`, addr.addressTitle || '', { shouldDirty: false });
        setValue(`addresses.${idx}.consigneeName`, addr.consigneeName || '', { shouldDirty: false });
        setValue(`addresses.${idx}.consigneeGstin`, addr.consigneeGstin || '', { shouldDirty: false });
        setValue(`addresses.${idx}.country`, addr.country, { shouldDirty: false });
        setValue(`addresses.${idx}.state`, addr.state || '', { shouldDirty: false });
        setValue(`addresses.${idx}.district`, addr.district || '', { shouldDirty: false });
        setValue(`addresses.${idx}.address`, addr.address || '', { shouldDirty: false });
        setValue(`addresses.${idx}.city`, addr.city || '', { shouldDirty: false });
        setValue(`addresses.${idx}.pincode`, addr.pincode || '', { shouldDirty: false });
        setValue(`addresses.${idx}.isDefault`, addr.isDefault === 1 || addr.isDefault === true ? 1 : 0, { shouldDirty: false });
        setValue(`addresses.${idx}.expiredtimestamp`, addr.expiredtimestamp ?? 0, { shouldDirty: false });
      });

      // Call pincode API for each row that has 6-digit pincode (IndiaState) - edit mode: only update city options
      normalizedAddresses.forEach((addr, idx) => {
        const trimmedPin = (addr.pincode || '').replace(/\D/g, '');
        if (addr.country === 'IndiaState' && trimmedPin.length === 6) {
          pincodeData({ pincode: trimmedPin }, true)
            .then(({ data }) => {
              if (data) {
                setCityOptions((prev) => ({ ...prev, [idx]: data?.City?.[0] ? data.City : [] }));
                syncToFormik();
              }
            })
            .catch(() => {});
        }
      });

      isInitializedRef.current = true;
    }

    // Also handle case where Formik values change externally (e.g., when loading existing data)
    // This handles the case when enabling edit mode - Formik gets populated after component is initialized
    else if (formikAddresses.length > 0) {
      console.log('Checking if should update - formikAddresses.length > 0, isInitialized:', isInitializedRef.current);

      // Check if current values are empty or different from Formik values
      const hasEmptyCurrentValues =
        !currentValues ||
        currentValues.length === 0 ||
        (currentValues.length === 1 &&
          !currentValues[0]?.addressTitle &&
          !currentValues[0]?.consigneeName &&
          !currentValues[0]?.address &&
          !currentValues[0]?.city &&
          !currentValues[0]?.pincode);

      // Check if values are actually different (deep comparison)
      // Normalize both for comparison to handle isDefault boolean vs numeric

      const normalizedCurrent = normalizeAddresses(currentValues || []);
      const normalizedFormik = normalizeAddresses(formikAddresses);
      const currentDataStr = JSON.stringify(normalizedCurrent);

      // Only load if current form data is different from Formik data
      if (currentDataStr !== formikDataStr) {
        console.log('Loading Formik values into React Hook Form:', formikAddresses);
        isSyncingRef.current = true;
        const normalizedAddresses = normalizeAddresses(formikAddresses);

        // First replace to update fields array
        replace(normalizedAddresses);
        // Use a small delay to ensure replace completes, then reset and set values
        setTimeout(() => {
          // Then reset form values
          reset({ addresses: normalizedAddresses }, { keepDefaultValues: false });

          // Manually set each field to ensure values are set
          normalizedAddresses.forEach((addr, idx) => {
            setValue(`addresses.${idx}.addressTitle`, addr.addressTitle || '', { shouldDirty: false });
            setValue(`addresses.${idx}.consigneeName`, addr.consigneeName || '', { shouldDirty: false });
            setValue(`addresses.${idx}.consigneeGstin`, addr.consigneeGstin || '', { shouldDirty: false });
            setValue(`addresses.${idx}.country`, addr.country, { shouldDirty: false });
            setValue(`addresses.${idx}.state`, addr.state || '', { shouldDirty: false });
            setValue(`addresses.${idx}.district`, addr.district || '', { shouldDirty: false });
            setValue(`addresses.${idx}.address`, addr.address || '', { shouldDirty: false });
            setValue(`addresses.${idx}.city`, addr.city || '', { shouldDirty: false });
            setValue(`addresses.${idx}.pincode`, addr.pincode || '', { shouldDirty: false });
            setValue(`addresses.${idx}.isDefault`, addr.isDefault === 1 || addr.isDefault === true ? 1 : 0, { shouldDirty: false });
            setValue(`addresses.${idx}.expiredtimestamp`, addr.expiredtimestamp ?? 0, { shouldDirty: false });
          });

          // Call pincode API for each row that has 6-digit pincode (IndiaState) - edit mode: only update city options
          normalizedAddresses.forEach((addr, idx) => {
            const trimmedPin = (addr.pincode || '').replace(/\D/g, '');
            if (addr.country === 'IndiaState' && trimmedPin.length === 6) {
              pincodeData({ pincode: trimmedPin }, true)
                .then(({ data }) => {
                  if (data) {
                    setCityOptions((prev) => ({ ...prev, [idx]: data?.City?.[0] ? data.City : [] }));
                    syncToFormik();
                  }
                })
                .catch(() => {});
            }
          });

          // Mark this data as loaded
          loadedFormikDataRef.current = formikDataStr;
          isInitializedRef.current = true;
          setTimeout(() => {
            isSyncingRef.current = false;
          }, 50);
        }, 10);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.deliveryAddresses?.length, sameAsAbove, resetbool, viewPage, formikDeliveryKey]);

  // Function to sync values to Formik immediately
  const syncToFormik = useCallback(() => {
    // if (formik && !isSyncingRef.current) {
    const currentValues = watch('addresses');
    if (currentValues) {
      // Mark as initialized on first sync
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }

      isSyncingRef.current = true;
      // Normalize isDefault values to ensure they're numeric (1/0)
      const normalizedValues = normalizeAddresses(currentValues);
      console.log(currentValues, 'currentValues');

      formik.setFieldValue(fieldName, normalizedValues, false); // false = don't validate
      // Use setTimeout to allow Formik to process the update
      // setTimeout(() => {
      //   isSyncingRef.current = false;
      // }, 50);
    }
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName]);

  // Sync React Hook Form values to Formik whenever they change
  useEffect(() => {
    if (formik && values && !isSyncingRef.current) {
      // In view mode: do not overwrite Formik when it already has real delivery data (e.g. from getPartyMasterData).
      // Otherwise we overwrite with RHF's default row before async load completes and shipping addresses never show.
      if (viewPage) {
        const formikAddresses = formik?.values?.[fieldName];
        if (Array.isArray(formikAddresses) && formikAddresses.length > 0) {
          const first = formikAddresses[0];
          const formikHasRealData =
            (first?.address && first.address.trim()) ||
            (first?.city && first.city.trim()) ||
            (first?.addressTitle && first.addressTitle.trim()) ||
            (first?.pincode && first.pincode.trim());
          if (formikHasRealData) {
            return; // keep Formik as source of truth in view mode
          }
        }
      }

      // Mark as initialized on first change (user interaction or programmatic)
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }

      isSyncingRef.current = true;
      // Normalize isDefault values to ensure they're numeric (1/0)
      const normalizedValues = normalizeAddresses(values);
      formik.setFieldValue(fieldName, normalizedValues, false); // false = don't validate
      // Use setTimeout to allow Formik to process the update
      // setTimeout(() => {
      //   isSyncingRef.current = false;
      // }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  // Sync billing address changes to first active row when "same as above" is checked
  useEffect(() => {
    if (sameAsAbove === false) {
      // When "same as above" is disabled, clear stale Formik error at index 0.
      const fieldErrors = formik?.errors?.[fieldName];
      if (Array.isArray(fieldErrors) && fieldErrors[0]) {
        const updatedFieldErrors = [...fieldErrors];
        const firstRowError = updatedFieldErrors[0];

        if (firstRowError && typeof firstRowError === 'object') {
          const { godown, ...restErrors } = firstRowError;
          updatedFieldErrors[0] = Object.keys(restErrors).length > 0 ? restErrors : undefined;
        } else {
          updatedFieldErrors[0] = undefined;
        }

        formik.setErrors({
          ...formik.errors,
          [fieldName]: updatedFieldErrors,
        });
      }
      return;
    }

    const currentValues = watch('addresses');
    if (!currentValues || currentValues.length === 0) {
      return;
    }

    // Find the first active (non-expired) row index
    let firstActiveRowIndex = null;
    for (let i = 0; i < currentValues.length; i++) {
      const expiredtimestamp = currentValues[i]?.expiredtimestamp ?? 0;
      if (expiredtimestamp === 0) {
        firstActiveRowIndex = i;
        break;
      }
    }

    // If no active row found, return
    if (firstActiveRowIndex === null) {
      return;
    }

    // Get billing address values from Formik
    const billingCity = formik.values.City || '';
    const billingAddress = formik.values.billingAddress || '';
    const billingPincode = formik.values.Pincode || '';
    const billingCountry = formik.values.Country;
    const billingConsigneeName = formik.values.legalName || '';
    const billingConsigneeGstin = formik.values.gstin || '';

    // For India: use State and District; for foreign: use foreignstate and foreigndistrict
    let formattedBillingState, billingDistrict;
    if (billingCountry === 'IndiaState') {
      formattedBillingState = formik.values.State || '';
      if (formattedBillingState && !formattedBillingState.endsWith('District')) {
        formattedBillingState = `${formattedBillingState}District`;
      }
      billingDistrict = formik.values.District || '';
    } else {
      formattedBillingState = formik.values.foreignstate || '';
      billingDistrict = formik.values.foreigndistrict || '';
    }

    // Get first active row
    const firstRow = currentValues[firstActiveRowIndex];
    if (!firstRow) {
      return;
    }

    // Check if any billing address field has changed
    const hasChanged =
      firstRow.city !== billingCity ||
      firstRow.address !== billingAddress ||
      firstRow.pincode !== billingPincode ||
      firstRow.state !== formattedBillingState ||
      firstRow.district !== billingDistrict ||
      firstRow.country !== billingCountry ||
      firstRow.consigneeName !== billingConsigneeName ||
      firstRow.consigneeGstin !== billingConsigneeGstin;

    // Only update if something changed
    if (hasChanged) {
      isSyncingRef.current = true;

      // Update first active row with billing address values, preserving nickname and isDefault
      setValue(`addresses.${firstActiveRowIndex}.city`, billingCity, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.address`, billingAddress, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.pincode`, billingPincode, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.state`, formattedBillingState, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.district`, billingDistrict, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.country`, billingCountry, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.consigneeName`, billingConsigneeName, { shouldDirty: false });
      setValue(`addresses.${firstActiveRowIndex}.consigneeGstin`, billingConsigneeGstin, { shouldDirty: false });

      // Sync to Formik after a short delay
      setTimeout(() => {
        syncToFormik();
        isSyncingRef.current = false;
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sameAsAbove,
    formik?.values?.City,
    formik?.values?.billingAddress,
    formik?.values?.Pincode,
    formik?.values?.State,
    formik?.values?.District,
    formik?.values?.Country,
    formik?.values?.foreignstate,
    formik?.values?.foreigndistrict,
    formik?.values?.legalName,
    formik?.values?.gstin,
    values,
  ]);

  // Effect to set default country value to 'IndiaState' for new rows only
  // In view/edit mode, don't override existing country values
  useEffect(() => {
    if (values && values.length > 0 && !id) {
      values.forEach((row, index) => {
        const expiredtimestamp = row?.expiredtimestamp ?? 0;
        // Only set default for active rows that don't have a country value
        if (expiredtimestamp === 0 && (!row.country || row.country === '')) {
          setValue(`addresses.${index}.country`, 'IndiaState', { shouldDirty: false });
        }
      });
    }
  }, [values, viewPage, setValue]);

  // Effect to clean up state objects for deleted rows and ensure indices are correct
  useEffect(() => {
    if (!values || values.length === 0) return;

    // Build a map of active (non-expired) row indices
    const activeIndices = new Set();
    values.forEach((row, index) => {
      const expiredtimestamp = row?.expiredtimestamp ?? 0;
      if (expiredtimestamp === 0) {
        activeIndices.add(index);
      }
    });

    // Clean up state objects for deleted rows, but keep them for active rows
    // This ensures that state objects are only cleared for expired rows
    setStateLists((prev) => {
      const updated = { ...prev };
      // Remove state for expired rows
      Object.keys(updated).forEach((key) => {
        const idx = parseInt(key);
        if (!activeIndices.has(idx)) {
          delete updated[idx];
        }
      });
      return updated;
    });

    setDistrictsLists((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        const idx = parseInt(key);
        if (!activeIndices.has(idx)) {
          delete updated[idx];
        }
      });
      return updated;
    });

    setCityOptions((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        const idx = parseInt(key);
        if (!activeIndices.has(idx)) {
          delete updated[idx];
        }
      });
      return updated;
    });

    setPreviousStates((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        const idx = parseInt(key);
        if (!activeIndices.has(idx)) {
          delete updated[idx];
        }
      });
      return updated;
    });

    // Clean up refs for deleted rows
    Object.keys(cityRefs.current).forEach((key) => {
      const idx = parseInt(key);
      if (!activeIndices.has(idx)) {
        delete cityRefs.current[idx];
      }
    });

    Object.keys(addressTitleRefs.current).forEach((key) => {
      const idx = parseInt(key);
      if (!activeIndices.has(idx)) {
        delete addressTitleRefs.current[idx];
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  // Effect to fetch districts when state exists for any row (only when districts are missing)
  // This prevents performance issues by not refetching on every state change
  // Exception: For first active row, if state changes and previous state was different, clear and refetch
  // This handles the "Same as above" checkbox case
  useEffect(() => {
    const fetchDistrictsForRows = async () => {
      // Find the first active (non-expired) row index
      let firstActiveRowIndex = null;
      for (let i = 0; i < values.length; i++) {
        const expiredtimestamp = values[i]?.expiredtimestamp ?? 0;
        if (expiredtimestamp === 0) {
          firstActiveRowIndex = i;
          break;
        }
      }

      const promises = [];
      for (let i = 0; i < values.length; i++) {
        const row = values[i];
        if (row?.state && row?.country === 'IndiaState') {
          const previousState = previousStates[i];
          const stateChanged = previousState !== row.state;

          // For first active row, if state changed, clear districts to force refetch
          // This handles "Same as above" checkbox case
          // Note: We preserve the district value - only clear the districts list
          if (i === firstActiveRowIndex && stateChanged && previousState) {
            setDistrictsLists((prev) => {
              const updated = { ...prev };
              delete updated[i];
              return updated;
            });
            // Clear previous state so it will be treated as a new state
            setPreviousStates((prev) => {
              const updated = { ...prev };
              delete updated[i];
              return updated;
            });
          }

          // Only fetch if we don't already have districts for this row
          const currentDistricts = districtsLists[i];
          const needsFetch = !currentDistricts || currentDistricts.length === 0;

          if (needsFetch) {
            // For first active row, store the current district value before fetching
            const currentDistrictValue = i === firstActiveRowIndex ? row.district : null;

            promises.push(
              getDistrictOptions({ codetype: row.state }, true)
                .then((districtResponse) => {
                  if (districtResponse.data) {
                    setDistrictsLists((prev) => ({
                      ...prev,
                      [i]: districtResponse.data,
                    }));
                    // Update previous state to track that we've fetched for this state
                    setPreviousStates((prev) => ({
                      ...prev,
                      [i]: row.state,
                    }));

                    // For first active row, after fetching districts, ensure the district value is preserved and matches
                    // This handles the "Same as above" case where district value needs to match the list format
                    if (i === firstActiveRowIndex && currentDistrictValue) {
                      // Use setTimeout to ensure districts list state is updated before checking
                      setTimeout(() => {
                        if (currentDistrictValue) {
                          // Check if the district value exists in the new districts list
                          const districtExists = districtResponse.data.some(
                            (dist) => dist.value === currentDistrictValue || dist.label === currentDistrictValue
                          );
                          // If the district value doesn't match exactly, try to find a match
                          if (!districtExists) {
                            // Try to find a district that matches (handles "DistrictNameDistrict" format variations)
                            const matchingDistrict = districtResponse.data.find((dist) => {
                              const distValue = dist.value || '';
                              const distLabel = dist.label || '';
                              const currentValue = currentDistrictValue || '';

                              // Exact match
                              if (distValue === currentValue || distLabel === currentValue) return true;

                              // Handle "DistrictNameDistrict" format variations
                              const distBase = distValue.replace('District', '');
                              const currentBase = currentValue.replace('District', '');
                              if (
                                distBase === currentBase ||
                                distValue === `${currentBase}District` ||
                                currentValue === `${distBase}District`
                              ) {
                                return true;
                              }

                              return false;
                            });
                            if (matchingDistrict) {
                              // Update the district value to match the format in the list
                              setValue(`addresses.${i}.district`, matchingDistrict.value, { shouldDirty: false });
                            }
                          } else {
                            // District exists in list, ensure it's set correctly (preserve the value)
                            setValue(`addresses.${i}.district`, currentDistrictValue, { shouldDirty: false });
                          }
                        }
                      }, 100);
                    }
                  }
                })
                .catch((error) => {
                  console.error(`Error fetching districts for row ${i}:`, error);
                })
            );
          }
        }
      }
      await Promise.all(promises);
    };

    if (values && values.length > 0) {
      fetchDistrictsForRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.map((v, idx) => `${idx}-${v?.state}-${v?.country}`).join('|')]);

  // Add Row with validation
  const handleAddRow = async () => {
    const lastIndex = fields.length - 1;
    const fieldName = `addresses.${lastIndex}.addressTitle`;

    // Get current values of the last row to check if it's completely empty
    const lastRow = watch(`addresses.${lastIndex}`);
    if (lastRow?.addressTitle !== '') {
      if (activeRowCount < MAX_ROWS) {
        const newRowIndex = fields.length; // Index of the new row that will be added
        append({
          addressTitle: '',
          consigneeName: '',
          consigneeGstin: '',
          country: 'IndiaState',
          state: '',
          district: '',
          address: '',
          city: '',
          pincode: '',
          isDefault: 0,
          expiredtimestamp: 0,
        });

        // Reorder array: active rows (expiredtimestamp === 0) first, expired rows last
        setTimeout(() => {
          const currentValues = getValues('addresses') || [];
          const activeRows = [];

          currentValues.forEach((row) => {
            const expiredtimestamp = row?.expiredtimestamp ?? 0;
            if (expiredtimestamp === 0) {
              activeRows.push(row);
            }
          });

          // Combine: active rows first, then expired rows
          const reorderedArray = [...activeRows];

          // Update the entire array with reordered values
          replace(reorderedArray);

          // Find the new row index after reordering (it should be in activeRows)
          const newRowIndexAfterReorder = activeRows.length - 1;

          // Focus on the newly added row's title field after a short delay to ensure it's rendered
          const attemptFocus = (retryCount = 0) => {
            const maxRetries = 5;
            const retryDelay = 150;
            const fieldName = `addresses.${newRowIndexAfterReorder}.addressTitle`;
            if (addressTitleRefs.current[newRowIndexAfterReorder]?.current) {
              // Ref is ready, focus on it
              addressTitleRefs.current[newRowIndexAfterReorder].current.focus();
            } else if (retryCount < maxRetries) {
              // Ref not ready yet, retry after a delay
              setTimeout(() => {
                attemptFocus(retryCount + 1);
              }, retryDelay);
            } else {
              // Max retries reached, try using setFocus as fallback
              setFocus(fieldName);
            }
          };
          setTimeout(() => {
            attemptFocus();
          }, 100);
        }, 50);
      }
      return;
    } else {
      showWarningSnackbar('Please provide Branch/ Godown Name');
    }

    // If last row has some values, validate addressTitle
    const valid = await trigger(fieldName);

    if (!valid) {
      // Get current value
      const currentValue = watch(fieldName);

      // Mark field as touched and set error if empty
      if (!currentValue || !currentValue.trim()) {
        setValue(fieldName, currentValue || '', { shouldTouch: true, shouldValidate: true });
        setError(fieldName, {
          type: 'required',
          message: 'Branch/ Godown Name is required',
        });
      } else {
        setError(fieldName, {});
      }

      // Focus on the field to show error message
      setFocus(fieldName);
      return;
    }

    // Validation passed, add new row
    if (activeRowCount < MAX_ROWS) {
      append({
        addressTitle: '',
        consigneeName: '',
        consigneeGstin: '',
        country: 'IndiaState',
        state: '',
        district: '',
        address: '',
        city: '',
        pincode: '',
        isDefault: 0,
        expiredtimestamp: 0,
      });

      // Reorder array: active rows (expiredtimestamp === 0) first, expired rows last
      setTimeout(() => {
        const currentValues = getValues('addresses') || [];
        const activeRows = [];
        const expiredRows = [];

        currentValues.forEach((row) => {
          const expiredtimestamp = row?.expiredtimestamp ?? 0;
          if (expiredtimestamp === 0) {
            activeRows.push(row);
          } else {
            expiredRows.push(row);
          }
        });

        // Combine: active rows first, then expired rows
        const reorderedArray = [...activeRows, ...expiredRows];

        // Update the entire array with reordered values
        replace(reorderedArray);

        // Find the new row index after reordering (it should be in activeRows)
        const newRowIndexAfterReorder = activeRows.length - 1;

        // Focus on the newly added row's title field after a short delay to ensure it's rendered
        const attemptFocus = (retryCount = 0) => {
          const maxRetries = 5;
          const retryDelay = 150;
          const fieldName = `addresses.${newRowIndexAfterReorder}.addressTitle`;
          if (addressTitleRefs.current[newRowIndexAfterReorder]?.current) {
            // Ref is ready, focus on it
            addressTitleRefs.current[newRowIndexAfterReorder].current.focus();
          } else if (retryCount < maxRetries) {
            // Ref not ready yet, retry after a delay
            setTimeout(() => {
              attemptFocus(retryCount + 1);
            }, retryDelay);
          } else {
            // Max retries reached, try using setFocus as fallback
            setFocus(fieldName);
          }
        };
        setTimeout(() => {
          attemptFocus();
        }, 100);
      }, 50);
    }
  };

  const onSubmit = (data) => {
    // Sync to Formik before submit
    if (formik) {
      // Normalize isDefault values to ensure they're numeric (1/0)
      const normalizedAddresses = normalizeAddresses(data.addresses);
      formik.setFieldValue(fieldName, normalizedAddresses);
    }
  };

  // Effect to focus on nickname when focusRowIndex is set
  useEffect(() => {
    if (sameAsAbove === false) {
      const addresses = getValues('addresses') || [];
      const firstActiveRowIndex = addresses.findIndex((addr) => (addr?.expiredtimestamp ?? 0) === 0);
      const targetIndex = firstActiveRowIndex >= 0 ? firstActiveRowIndex : 0;
      clearErrors(`addresses.${targetIndex}.addressTitle`);
      return;
    }

    if (focusRowIndex !== null) {
      // Find the actual index in React Hook Form addresses array
      // The focusRowIndex is from Formik's deliveryAddresses (visible row index)
      // We need to find the corresponding RHF index (excluding expired rows)
      const rhfAddresses = watch('addresses') || [];

      // Find the row in RHF that corresponds to the Formik index
      // Count only visible (non-expired) rows
      let visibleIndex = 0;
      let rhfIndex = null;

      for (let i = 0; i < rhfAddresses.length; i++) {
        const rowValue = watch(`addresses.${i}`);
        const expiredtimestamp = rowValue?.expiredtimestamp ?? 0;

        if (expiredtimestamp === 0) {
          if (visibleIndex === focusRowIndex) {
            rhfIndex = i;
            break;
          }
          visibleIndex++;
        }
      }

      // If we found the index, focus on it
      if (rhfIndex !== null) {
        // Retry mechanism to wait for refs to be ready after reindexing
        const attemptFocus = (retryCount = 0) => {
          const maxRetries = 5;
          const retryDelay = 150;

          if (addressTitleRefs.current[rhfIndex]?.current) {
            // Ref is ready, focus on it
            addressTitleRefs.current[rhfIndex].current.focus();
            // Also trigger validation to show error
            trigger(`addresses.${rhfIndex}.addressTitle`);
            // Set error to highlight the field
            setError(`addresses.${rhfIndex}.addressTitle`, {
              type: 'required',
              message: 'Branch/ Godown Name is required',
            });
          } else if (retryCount < maxRetries) {
            // Ref not ready yet, retry after a delay
            setTimeout(() => {
              attemptFocus(retryCount + 1);
            }, retryDelay);
          } else {
            // Max retries reached, try using setFocus as fallback
            setFocus(`addresses.${rhfIndex}.addressTitle`);
            trigger(`addresses.${rhfIndex}.addressTitle`);
            setError(`addresses.${rhfIndex}.addressTitle`, {
              type: 'required',
              message: 'Branch/ Godown Name is required',
            });
          }
        };

        // Start attempting to focus after a small delay
        setTimeout(() => {
          attemptFocus();
        }, 100);
      }
    }
  }, [focusRowIndex, trigger, setError, setFocus, watch, values, sameAsAbove, clearErrors, getValues]);

  return (
    <Box sx={{ p:{xs:1, sm:2}, width: { xs: '90%', md: '100%' }, overflowX: 'auto', zoom:{xs:0.9, sm:1} }}>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* <Typography variant="h6" mb={2}>
        Address List
      </Typography> */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Table
          size="small"
          sx={{
            borderCollapse: 'collapse',
            '& .MuiTableCell-root': {
              border: '1px solid #ddd',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">Default</TableCell>
              <TableCell
                sx={{
                  minWidth: 200,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Branch/ Godown Name
                  <Typography
                    component="span"
                    sx={{
                      color: 'danger.main',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      ml: 0.5,
                    }}
                  >
                    *
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ minWidth: 160 }}>Company GSTIN</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Company Name</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Country</TableCell>
              <TableCell sx={{ minWidth: 90 }}>Pincode</TableCell>
              <TableCell sx={{ minWidth: 220 }}>State</TableCell>
              <TableCell sx={{ minWidth: 220 }}>District</TableCell>
              <TableCell sx={{ minWidth: 500 }}>Address</TableCell>
              <TableCell sx={{ minWidth: 150 }}>City</TableCell>
              <TableCell align="left" sx={{ minWidth: 80 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {fields
              .map((row, index) => {
                // Get the expiredtimestamp value for this row
                const rowValue = watch(`addresses.${index}`);
                const expiredtimestamp = rowValue?.expiredtimestamp ?? 0;

                // Only display rows with expiredtimestamp === 0
                if (expiredtimestamp !== 0) {
                  return null;
                }

                // Create or get ref for this row's city field
                if (!cityRefs.current[index]) {
                  cityRefs.current[index] = React.createRef();
                }
                console.log(row, 'rowrowrowrow');

                return (
                  <AddressRow
                    key={row.id}
                    index={index}
                    control={control}
                    errors={errors.addresses}
                    setValue={setValue}
                    getValues={getValues}
                    trigger={trigger}
                    remove={remove}
                    clearErrors={clearErrors}
                    setError={setError}
                    total={
                      fields.filter((r, i) => {
                        const rValue = watch(`addresses.${i}`);
                        return (rValue?.expiredtimestamp ?? 0) === 0;
                      }).length
                    }
                    stateLists={stateLists}
                    setStateLists={setStateLists}
                    districtsLists={districtsLists}
                    setDistrictsLists={setDistrictsLists}
                    getDistrictOptions={getDistrictOptions}
                    cityOptions={cityOptions}
                    setCityOptions={setCityOptions}
                    previousStates={previousStates}
                    setPreviousStates={setPreviousStates}
                    pincodeData={pincodeData}
                    getDistrictOptionsForState={getDistrictOptionsForState}
                    cityRef={cityRefs.current[index]}
                    syncToFormik={syncToFormik}
                    viewPage={viewPage}
                    sameAsAbove={sameAsAbove}
                    addressTitleRefs={addressTitleRefs}
                    allAddresses={values}
                    onSameAsAboveChange={onSameAsAboveChange}
                    replace={replace}
                    setBackdropOpen={setBackdropOpen}
                    addressMaxLength={addressMaxLength}
                  />
                );
              })
              .filter(Boolean)}
          </TableBody>
        </Table>
        <Button
          disableRipple
          disableElevation
          size="small"
          variant="text"
          disabled={viewPage || activeRowCount >= MAX_ROWS}
          onClick={handleAddRow}
          startIcon={<AddBox />}
          sx={{
            fontSize: '14px',
            marginTop: '15px',
          }}
        >
          Add New Line
        </Button>
        {/* <Button variant="contained" sx={{ mt: 2 }} disabled={fields.length >= MAX_ROWS} onClick={handleAddRow}>
          Add New Shipping Address
        </Button> */}
      </form>
    </Box>
  );
}
